import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Users, UserCheck, UserX, Phone, Stethoscope, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { updateDocument } from '@/services/firebaseService';

interface Appointment {
  id: string;
  patientName: string;
  phone: string;
  serviceType: string;
  date: Date;
  time: string;
  price: number;
  paymentStatus: 'paid' | 'pending' | 'transferred';
  remainingAmount: number;
  createdAt: Date;
  notes: string;
  queueNumber?: number;
  queueStatus?: 'waiting' | 'in-progress' | 'completed' | 'cancelled';
}

interface QueueSystemProps {
  appointments: Appointment[];
}

export default function QueueSystem({ appointments }: QueueSystemProps) {
  const { toast } = useToast();
  const [queueAppointments, setQueueAppointments] = useState<Appointment[]>([]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayAppointments = appointments.filter(app => {
      const appDate = new Date(app.date);
      return appDate >= today;
    });
    const appointmentsWithQueue = todayAppointments.map((app, index) => ({
      ...app,
      queueNumber: app.queueNumber || index + 1,
      queueStatus: app.queueStatus || 'waiting'
    }));
    setQueueAppointments(appointmentsWithQueue);
  }, [appointments]);

  const updateQueueStatus = async (appointmentId: string, newStatus: 'waiting' | 'in-progress' | 'completed' | 'cancelled') => {
    try {
      await updateDocument('clinicAppointments', appointmentId, {
        queueStatus: newStatus,
        updatedAt: new Date()
      });
      setQueueAppointments(prev =>
        prev.map(app =>
          app.id === appointmentId
            ? { ...app, queueStatus: newStatus }
            : app
        )
      );
      const appointment = queueAppointments.find(a => a.id === appointmentId);
      if (appointment) {
        toast({
          title: "تم التحديث",
          description: `تم تحديث حالة ${appointment.patientName} إلى ${getStatusText(newStatus)}`
        });
      }
    } catch (error) {
      console.error('Error updating queue status:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة المريض"
      });
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'waiting': return 'في الانتظار';
      case 'in-progress': return 'قيد المعالجة';
      case 'completed': return 'مكتمل';
      case 'cancelled': return 'ملغي';
      default: return 'في الانتظار';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'bg-yellow-500';
      case 'in-progress': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const waitingCount = queueAppointments.filter(app => app.queueStatus === 'waiting').length;
  const inProgressCount = queueAppointments.filter(app => app.queueStatus === 'in-progress').length;
  const completedCount = queueAppointments.filter(app => app.queueStatus === 'completed').length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">إجمالي المرضى</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{queueAppointments.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">في الانتظار</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{waitingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">قيد المعالجة</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{inProgressCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">مكتمل</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedCount}</div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            قائمة الدور - {format(new Date(), 'dd MMMM yyyy', { locale: ar })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {queueAppointments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>لا يوجد مرضى في الدور اليوم</p>
            </div>
          ) : (
            <div className="space-y-4">
              {queueAppointments
                .sort((a, b) => (a.queueNumber || 0) - (b.queueNumber || 0))
                .map((appointment) => (
                  <Card key={appointment.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-bold text-blue-600">
                            {appointment.queueNumber}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{appointment.patientName}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {appointment.phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Stethoscope className="w-3 h-3" />
                                {appointment.serviceType}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {format(new Date(appointment.createdAt), 'HH:mm', { locale: ar })}
                              </span>
                            </div>
                            {appointment.notes && (
                              <div className="text-sm text-gray-500 mt-1">
                                {appointment.notes}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${getStatusColor(appointment.queueStatus || 'waiting')} text-white`}>
                            {getStatusText(appointment.queueStatus || 'waiting')}
                          </Badge>
                          <div className="flex items-center gap-1">
                            {appointment.queueStatus === 'waiting' && (
                              <Button
                                size="sm"
                                onClick={() => updateQueueStatus(appointment.id, 'in-progress')}
                                className="flex items-center gap-1"
                              >
                                <UserCheck className="w-3 h-3" />
                                بدء المعالجة
                              </Button>
                            )}
                            {appointment.queueStatus === 'in-progress' && (
                              <Button
                                size="sm"
                                onClick={() => updateQueueStatus(appointment.id, 'completed')}
                                className="flex items-center gap-1"
                              >
                                <UserCheck className="w-3 h-3" />
                                إنهاء المعالجة
                              </Button>
                            )}
                            {(appointment.queueStatus === 'waiting' || appointment.queueStatus === 'in-progress') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateQueueStatus(appointment.id, 'cancelled')}
                                className="flex items-center gap-1"
                              >
                                <UserX className="w-3 h-3" />
                                إلغاء
                              </Button>
                            )}
                            {appointment.queueStatus === 'cancelled' && (
                              <Button
                                size="sm"
                                onClick={() => updateQueueStatus(appointment.id, 'waiting')}
                                className="flex items-center gap-1"
                              >
                                <UserCheck className="w-3 h-3" />
                                إعادة تفعيل
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 